import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ImageOff, GripVertical, Maximize2, Minimize2, RefreshCw, X } from 'lucide-react';

/**
 * Asset3DReviewSplit — side-by-side 3D viewer | approved reference image.
 *
 * Founder spec (May 12, 2026, Sketchfab parity):
 *   Client wants to compare the in-review 3D model with the approved
 *   concept art on a SINGLE screen. The split must be draggable so the
 *   reviewer can grow the 3D viewer when they need to scrub the topology
 *   AND shrink it when they want to study the reference art.
 *
 * Behaviour:
 *   • Renders a 1/2/1/2 horizontal split by default (50/50).
 *   • Drag the grip in the middle to resize. Snaps to 25 / 50 / 75 with
 *     a 4% slop zone so clicks land cleanly.
 *   • Double-click the grip to reset to 50/50.
 *   • Hide button on the reference panel collapses to a 100% 3D view.
 *   • Mobile / narrow viewports (<900px) stack vertically — the splitter
 *     becomes a horizontal handle.
 *   • Header on the reference side shows kind/stage/round + filename.
 *   • If no reference exists, renders just the children (3D viewer alone).
 */
const SNAP_PCTS = [25, 50, 75];
const SNAP_SLOP = 4;        // ± percent where we snap

export default function Asset3DReviewSplit({ children, reference, onReload, onHide }) {
  const containerRef = useRef(null);
  // Persist last split width so it survives unmount/remount within a
  // single review session.
  const [splitPct, setSplitPct] = useState(() => {
    const saved = typeof window !== 'undefined' ? window.sessionStorage.getItem('asset3d-split-pct') : null;
    const n = parseFloat(saved || '');
    return Number.isFinite(n) && n > 10 && n < 90 ? n : 60;  // bias toward 3D
  });
  const [dragging, setDragging] = useState(false);
  const [stackVertically, setStackVertically] = useState(false);
  const [showRef, setShowRef] = useState(true);

  // Watch container width for responsive vertical-stack swap.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(entries => {
      const w = entries[0].contentRect.width;
      setStackVertically(w < 900);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (ev) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      let raw;
      if (stackVertically) {
        raw = ((clientY - rect.top) / rect.height) * 100;
      } else {
        raw = ((clientX - rect.left) / rect.width) * 100;
      }
      raw = Math.max(20, Math.min(85, raw));
      // Snap behaviour.
      let next = raw;
      for (const s of SNAP_PCTS) {
        if (Math.abs(raw - s) < SNAP_SLOP) { next = s; break; }
      }
      setSplitPct(next);
    };
    const onUp = () => {
      setDragging(false);
      window.sessionStorage.setItem('asset3d-split-pct', String(splitPct));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, stackVertically, splitPct]);

  // No reference → render the 3D viewer alone.
  if (!reference || !reference.cloud_url) {
    return <div className="w-full h-full">{children}</div>;
  }

  // Reference collapsed → 100% 3D viewer with a "Show reference" pill.
  if (!showRef) {
    return (
      <div className="relative w-full h-full" data-testid="asset3d-split-collapsed">
        {children}
        <button
          onClick={() => setShowRef(true)}
          data-testid="asset3d-show-reference"
          className="absolute top-2 right-2 z-30 h-8 px-3 bg-amber-500/95 hover:bg-amber-400 text-zinc-950 text-[10px] font-bold rounded-md flex items-center gap-1.5 shadow-lg"
        >
          <Maximize2 className="w-3 h-3" /> Show Reference
        </button>
      </div>
    );
  }

  const flexDir = stackVertically ? 'flex-col' : 'flex-row';

  return (
    <div
      ref={containerRef}
      className={`w-full h-full flex ${flexDir} bg-black select-none`}
      data-testid="asset3d-split"
      style={{ cursor: dragging ? (stackVertically ? 'row-resize' : 'col-resize') : 'default' }}
    >
      {/* 3D viewer pane */}
      <div
        className="relative min-h-0 min-w-0"
        style={stackVertically
          ? { height: `${splitPct}%`, width: '100%' }
          : { width: `${splitPct}%`, height: '100%' }}
        data-testid="asset3d-split-3d"
      >
        {children}
      </div>

      {/* Draggable splitter */}
      <div
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        onDoubleClick={() => setSplitPct(50)}
        data-testid="asset3d-split-divider"
        title="Drag to resize · Double-click to reset"
        className={`shrink-0 relative group bg-zinc-800 hover:bg-amber-500/40 ${dragging ? '!bg-amber-500/60' : ''} transition-colors ${
          stackVertically
            ? 'h-1.5 w-full cursor-row-resize'
            : 'w-1.5 h-full cursor-col-resize'
        }`}
      >
        <div className={`absolute inset-0 flex items-center justify-center pointer-events-none ${dragging || 'opacity-60 group-hover:opacity-100'}`}>
          <div className={`bg-zinc-700 group-hover:bg-amber-500 rounded ${stackVertically ? 'w-10 h-1' : 'w-1 h-10'} transition-colors`} />
        </div>
      </div>

      {/* Reference pane */}
      <div
        className="relative min-h-0 min-w-0 bg-zinc-950 flex flex-col border-l border-zinc-800"
        style={stackVertically
          ? { height: `${100 - splitPct}%`, width: '100%' }
          : { width: `${100 - splitPct}%`, height: '100%' }}
        data-testid="asset3d-split-ref"
      >
        <div className="shrink-0 px-3 py-1.5 flex items-center justify-between gap-2 bg-zinc-900 border-b border-zinc-800">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shrink-0">
              Approved
            </span>
            <span className="text-[10px] uppercase tracking-wider text-zinc-400 shrink-0">
              {(reference.stage || 'Reference').replace(/_/g, ' ')}
              {reference.round ? ` · v${String(reference.round).padStart(2, '0')}` : ''}
            </span>
            {reference.filename && (
              <span className="text-[10px] text-zinc-500 font-mono truncate" title={reference.filename}>
                {reference.filename}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {onReload && (
              <button
                onClick={onReload}
                data-testid="asset3d-ref-reload"
                title="Refetch latest approved reference"
                className="h-6 w-6 flex items-center justify-center text-zinc-500 hover:text-amber-400"
              >
                <RefreshCw className="w-3 h-3" />
              </button>
            )}
            <button
              onClick={() => { setShowRef(false); onHide?.(); }}
              data-testid="asset3d-ref-hide"
              title="Hide reference panel"
              className="h-6 w-6 flex items-center justify-center text-zinc-500 hover:text-rose-300"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 relative overflow-hidden flex items-center justify-center bg-[#0c0c0e]">
          <img
            src={reference.cloud_url}
            alt={reference.filename || 'Approved reference'}
            data-testid="asset3d-ref-image"
            className="max-w-full max-h-full object-contain pointer-events-none"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>

        {reference.nas_path && (
          <div className="shrink-0 px-3 py-1 bg-zinc-900 border-t border-zinc-800 text-[9px] text-zinc-600 font-mono truncate" title={reference.nas_path}>
            NAS: {reference.nas_path}
          </div>
        )}
      </div>
    </div>
  );
}

export function Asset3DReviewEmpty() {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="flex flex-col items-center gap-2 text-zinc-600">
        <ImageOff className="w-8 h-8" />
        <p className="text-[10px]">No approved reference for this asset</p>
      </div>
    </div>
  );
}
